import React, { useState } from "react";
import { Schedule, displayTime, shiftDate, whiteBadgeFill, roomDoorOrder } from "./api";
import { dateLabel } from "./Reports";
import { duration } from "./FrontDesk";
export function WorkloadPivot({ data }: { data: Schedule }) {
  const [selected, setSelected] = useState<{
    date: string;
    ruleId: string | null;
  } | null>(null);
  const dates = Array.from({ length: data.days }, (_, i) =>
    shiftDate(data.from, i),
  );
  const rules = data.rules
    .filter((r) => !r.retired)
    .sort((a, b) => a.rank - b.rank);
  const tasksFor = (date: string, ruleId: string | null) =>
    roomDoorOrder(data.rooms).flatMap((room) =>
      room.days
        .filter((day) => day.date === date)
        .flatMap((day) =>
          day.tasks
            .filter(
              (task) =>
                task.state !== "superseded" &&
                (!ruleId || task.ruleId === ruleId),
            )
            .map((task) => ({ room, day, task })),
        ),
    );
  const select = (date: string, ruleId: string | null) =>
    setSelected((old) =>
      old?.date === date && old.ruleId === ruleId ? null : { date, ruleId },
    );
  return (
    <>
      <div className="pfres-panel pfres-panel--flush hk-gridwrap">
        <table className="hk-week hk-pivot">
          <thead>
            <tr>
              <th className="hk-pinned">Clean</th>
              {dates.map((date) => (
                <th key={date}>
                  <button onClick={() => select(date, null)}>
                    {dateLabel(date)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <tr key={rule.id}>
                <th className="hk-pinned">
                  <span className="hk-dot" style={{ background: rule.color }} />
                  {rule.label}
                  <small className="d-block text-body-secondary">
                    {rule.minutes}m
                  </small>
                </th>
                {dates.map((date) => {
                  const tasks = tasksFor(date, rule.id);
                  return (
                    <td
                      key={date}
                      className={
                        selected?.date === date && selected.ruleId === rule.id
                          ? "hk-selected"
                          : ""
                      }
                    >
                      <button
                        disabled={!tasks.length}
                        aria-label={`${rule.code} ${date} tasks`}
                        onClick={() => select(date, rule.id)}
                      >
                        {tasks.length ? (
                          <>
                            <strong>{tasks.length}</strong>
                            <small>
                              {duration(
                                tasks.reduce((s, t) => s + t.task.minutes, 0),
                              )}
                            </small>
                          </>
                        ) : (
                          "-"
                        )}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>Cleans</th>
              {dates.map((date) => (
                <td key={date}>{tasksFor(date, null).length}</td>
              ))}
            </tr>
            <tr>
              <th>Time</th>
              {dates.map((date) => (
                <td key={date}>
                  {duration(
                    tasksFor(date, null).reduce(
                      (s, t) => s + t.task.minutes,
                      0,
                    ),
                  )}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      {selected ? (
        <section className="pfres-section hk-drill">
          <h2 className="pfres-section__subhead">
            {selected.ruleId
              ? rules.find((r) => r.id === selected.ruleId)?.label
              : "Full day"}{" "}
            · {dateLabel(selected.date)}
          </h2>
          <div className="hk-drill-cards">
            {tasksFor(selected.date, selected.ruleId).map(({ room, day, task }) => (
              <article
                key={`${room.roomId}-${task.taskId}`}
                className="hk-drill-card"
                style={{ borderLeftColor: task.color }}
              >
                <strong>{room.roomLabel}</strong>
                <div>{day.guestNames.join(", ")}</div>
                {day.checkInAt && <div>Check-in {displayTime(day.checkInAt, data.timezone)}</div>}
                {day.checkOutAt && <div>Check-out {displayTime(day.checkOutAt, data.timezone)}</div>}
                <div>
                  <span
                    className="hk-clean-chip"
                    style={{
                      background: whiteBadgeFill(task.color),
                      color: "white",
                    }}
                  >
                    {task.code}
                  </span>
                  <small>{task.triggerReason}</small>
                </div>
                <span>{task.minutes}m</span>
              </article>
            ))}
          </div>
        </section>
      ) : (
        <p className="text-body-secondary mt-3">
          Select a cell to see its rooms, or a day heading for the full day.
        </p>
      )}
    </>
  );
}
